import Image from "next/image";
import { renderAttribute } from "../SliderType/components/PopupSlider/utils/renderAttribute";
import clsx from "clsx";

export const CommonCard = ({
  onClick,
  imageSource,
  imageAlt,
  title,
  category,
  description,
  price,
  weight,
  calories,
  show,
  isBox = false,
  productSize = "small",
  ingredients,
  nutritionalInfo,
  allergens,
}: {
  onClick?: () => void;
  imageSource?: string | null;
  imageAlt?: string | null;
  title?: string | null;
  category?: string | null;
  description?: string | null;
  price?: number | null;
  weight?: number | null;
  calories?: number | null;
  show?: string[] | null;
  isBox?: boolean;
  productSize?: "small" | "medium" | "big";
  nutritionalInfo?: string | null;
  ingredients?: string[] | null;
  allergens?: string[] | null;
}) => {
  if (productSize === "big") {
    return (
      <div
        className={clsx(
          "flex-shrink-0 flex snap-mandatory snap-x flex-col w-[400px] relative h-full bg-white rounded-xl shadow-md hover:shadow-lg p-4 transition-shadow duration-300 border border-gray-100",
          onClick && "cursor-pointer",
          show?.includes("description") && description && "min-h-[420px]"
        )}
        {...(onClick && { onClick })}
      >
        <div className="w-full h-60 overflow-hidden rounded-lg shadow-lg relative hover:scale-105 transition-all duration-300">
          <Image
            src={imageSource || ""}
            alt={imageAlt || "image"}
            width={400}
            height={200}
            className="w-full h-full object-cover"
            quality={80}
          />
        </div>
        <div className="mt-4 flex flex-col justify-between h-full flex-1">
          <div className="space-y-4">
            {renderAttribute(show || [], "title", title, undefined, true, true)}
            {renderAttribute(show || [], "category", category)}
            {show?.includes("description") && (
              <>
                <span className="font-bold mt-2 text-sm">{description}</span>
                {show?.includes("calories") && (
                  <span className="text-sm font-bold">{calories} kcal</span>
                )}
              </>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              {show?.includes("price") && (
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold">{price}</span>
                  <span className="text-xl font-bold">Lei</span>
                </div>
              )}
              {show?.includes("weight") && (
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold">{weight}g</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (productSize === "medium") {
    return (
      <div
        className="w-full lg:w-[480px] flex-shrink-0 cursor-pointer bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
        onClick={onClick}
      >
        <div className="flex items-center p-2 lg:p-3 lg:pr-4 !pt-2 lg:pb-2 lg:pt-0 gap-2 h-full">
          <div className="flex-1 flex flex-col h-full lg:p-4 p-2 px-0 !pr-0 !p-0 pb-0">
            <div className="flex flex-col gap-0 justify-between h-full flex-1">
              <div>
                {show?.includes("title") && (
                  <span className="text-sm lg:text-lg font-bold text-gray-700 leading-tight line-clamp-2">
                    {title}
                  </span>
                )}
                {show?.includes("category") && (
                  <span className="block !text-sm mt-1">{category}</span>
                )}
              </div>

              {show?.includes("description") && (
                <div>
                  <span
                    className="text-gray-600 text-sm lg:text-base line-clamp-3"
                    style={{
                      lineHeight: 1,
                    }}
                  >
                    {description}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                {show?.includes("price") && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold">{price}</span>
                    <span className="text-xl font-bold">Lei</span>
                  </div>
                )}
                {weight && <span className="text-xl font-bold">{weight}g</span>}
              </div>
            </div>
          </div>
          {imageSource && (
            <div className="lg:w-[150px] hover:scale-105 transition-all duration-300 relative lg:h-[150px] w-[130px] h-[130px]">
              <Image
                src={imageSource || ""}
                alt={imageAlt || "image"}
                width={150}
                height={150}
                className="w-full h-full rounded-lg object-cover"
                quality={80}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "flex-shrink-0 w-44 lg:w-64 bg-white rounded-xl shadow-md hover:shadow-lg p-4 transition-shadow duration-300 border border-gray-100",
        onClick && "cursor-pointer"
      )}
      {...(onClick && { onClick })}
    >
      <div className="w-full h-40 overflow-hidden rounded-lg shadow-lg relative">
        <Image
          src={imageSource || ""}
          alt={imageAlt || "image"}
          width={150}
          height={150}
          className="w-full h-full object-cover"
          quality={80}
        />
      </div>
      <div className="mt-4">
        {show?.includes("title") && (
          <span className="text-sm lg:text-lg font-bold text-gray-700 leading-tight line-clamp-2">
            {title}
          </span>
        )}
        {renderAttribute(show || [], "category", category)}
        <div className="mt-2">
          {show?.includes("description") && (
            <span className="text-gray-600 text-xs lg:text-lg">
              {description}
            </span>
          )}
          <div className="flex items-center gap-2">
            {renderAttribute(show || [], "price", price, "lei")}
            {renderAttribute(show || [], "weight", weight, "g")}
          </div>
          {renderAttribute(show || [], "calories", calories, "kcal")}
        </div>
      </div>
    </div>
  );
};
